import { useState, useEffect } from "react";
import { Outlet, Navigate } from "react-router-dom";
import { Menu, Plus } from "lucide-react";
import AppSidebar from "../components/playful/AppSidebar";
import Topbar from "../components/app/Topbar";
import UniversalCreate from "../components/app/UniversalCreate";
import AccountBanners from "../components/app/AccountBanners";
import PromoBanner from "../components/playful/PromoBanner";
import { Wordmark } from "../components/playful/Brand";
import { useAuth } from "../context/AuthContext";

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const { user } = useAuth();

  // Global ⌘K / Ctrl+K opens Universal Create
  useEffect(() => {
    if (!user) return;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCreateOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user]);

  // No sidebar for unauthenticated users — just render the page full-width
  if (!user) {
    return (
      <main className="min-h-screen overflow-x-hidden bg-paper">
        <Outlet />
      </main>
    );
  }

  // New users complete onboarding before entering the app (admins skip it).
  if (!user.onboarding_completed && user.role !== "admin") {
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-paper">
      {/* Promo banner spans full width above the sidebar + content row */}
      <PromoBanner forceShow />
      <div className="flex min-h-0 flex-1 overflow-hidden">
      <AppSidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

      {/* Content column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Desktop topbar */}
        <Topbar onOpenCreate={() => setCreateOpen(true)} />

        {/* Mobile top bar */}
        <header
          className="sticky top-0 z-30 flex h-[57px] shrink-0 items-center gap-3 border-b-2 border-ink bg-white px-4 md:hidden"
          data-testid="mobile-topbar"
        >
          <button
            onClick={() => setMobileOpen(true)}
            className="rounded-xl border-2 border-ink p-2 shadow-sticker-xs transition-colors hover:bg-ink/5"
            aria-label="Open navigation"
          >
            <Menu size={17} strokeWidth={2.5} />
          </button>
          <Wordmark size="sm" tone="ink" accent="candy" />
          <button
            onClick={() => setCreateOpen(true)}
            className="candy-press ml-auto inline-flex items-center gap-1.5 rounded-full border-2 border-ink bg-candy px-3 py-1.5 font-display text-sm font-bold text-white shadow-sticker-xs"
            aria-label="Create"
            data-testid="mobile-create"
          >
            <Plus size={15} strokeWidth={3} /> Create
          </button>
        </header>

        {/* Scrollable page content */}
        <main className="flex-1 overflow-y-auto overflow-x-hidden bg-paper" id="app-main-scroll">
          <AccountBanners />
          <Outlet />
        </main>
      </div>

      <UniversalCreate open={createOpen} onClose={() => setCreateOpen(false)} />
      </div>
    </div>
  );
}
